import React from 'react';
import { T } from '@tolgee/react';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import { AdminContractsViolet } from '../../../../assets/Contracts/AdminContractsViolet';
import { Person } from '../../../../assets/Person';
import ProducerCompanyIcon from '../../../../assets/ProducerCompanyIcon';
import { NumericFormat } from 'react-number-format';
import { Calendar } from '../../../../assets/Calendar';
import dayjs from 'dayjs';
import DashedDivider from '../../../../components/DashedDivider/DashedDivider';
import { SuccessMessageProps } from '../addNewContract.interface';
import { numberTwoDecimal } from '../../../../utils/number.util';
import Flag from 'react-world-flags';

const SuccessMessage: React.FC<SuccessMessageProps> = ({
	buyerName,
	sellerName,
	productName,
	pricePerTonBuyer,
	pricePerTonSeller,
	markup,
	quantity,
	dateFrom,
	dateTo,
	buyerRepresentative,
	sellerRepresentative,
	onConfirm,
	storeName,
	storeAddress,
	storeCountryCode,
	countryCode,
	allowedOverdraftPercent,
}) => {
	const rawProfit =
		(Number(pricePerTonBuyer || 0) - Number(pricePerTonSeller || 0)) *
		Number(quantity || 0);

	const qtyNum = Number(quantity || 0);

	const overdraftRaw = String(allowedOverdraftPercent ?? '').trim();
	const overdraftPercent =
		overdraftRaw !== '' ? Number(overdraftRaw.replace(',', '.')) : NaN;

	const overdraftedQty =
		qtyNum > 0 && Number.isFinite(overdraftPercent)
			? qtyNum * (1 + overdraftPercent / 100)
			: null;

	const isLoss = rawProfit < 0;
	const computedProfit = Math.abs(rawProfit);

	return (
		<section className="w-full h-full flex items-center justify-center">
			<div className="w-[532px] h-fit flex flex-col border border-solid border-gray-10 rounded-[12px] box-sizing">
				<div className="m-3">
					<AdminContractsViolet />
				</div>

				<div className="flex flex-col gap-3 mx-3 mb-3">
					<SypacText variant="body-regular-medium">
						<p className="text-[20px] leading-[32px] text-gray-80">
							<T keyName="adminContracts.contractSuccessfullyCreated">
								Contract successfully created
							</T>
						</p>
					</SypacText>

					<div className="flex flex-col gap-3 p-3 bg-alabaster border border-dashed border-gray-10 rounded-10">
						<SypacText variant="body-regular-medium">
							<p className="text-[14px] leading-[16px] text-gray-40">
								<T keyName="adminContracts.contractDetails">
									Contract details:
								</T>
							</p>
						</SypacText>
						<DashedDivider dashLength={5} gapLength={5} />

						<div className="h-fit min-h-[16px] grid grid-cols-[126px,auto] gap-5 items-start">
							<div className="h-[16px] flex items-center gap-3">
								<Person color="#A2A2A2" />
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40 text-[14px] leading-[16px] mt-[2px]">
										<T keyName="adminContracts.buyer">Buyer</T>
									</p>
								</SypacText>
							</div>

							<div className="flex flex-col gap-3 items-start">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80 text-[14px] leading-[16px] mt-[2px]">
										{buyerName}
									</p>
								</SypacText>

								<SypacText variant="body-regular-medium">
									<p className="text-gray-50 text-[14px] leading-[16px]">
										{buyerRepresentative}
									</p>
								</SypacText>
							</div>
						</div>
						<DashedDivider dashLength={5} gapLength={5} />

						<div className="h-fit min-h-[16px] grid grid-cols-[126px,auto] gap-5 items-start">
							<div className="h-[16px] flex items-center gap-3">
								<ProducerCompanyIcon color="#A2A2A2" />
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40 text-[14px] leading-[16px] mt-[2px]">
										<T keyName="adminContracts.seller">Seller</T>
									</p>
								</SypacText>
							</div>

							<div className="flex flex-col gap-3 items-start">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80 text-[14px] leading-[16px] mt-[2px]">
										{sellerName}
									</p>
								</SypacText>

								<SypacText variant="body-regular-medium">
									<p className="text-gray-50 text-[14px] leading-[16px]">
										{sellerRepresentative}
									</p>
								</SypacText>

								{storeName ? (
									<div className="flex items-start gap-3">
										<SypacText variant="body-regular-medium">
											<p className="text-gray-50 text-[14px] leading-[16px] whitespace-nowrap">
												{storeName}
											</p>
										</SypacText>
										<div className="w-[2px] h-[2px] bg-gray-60 rounded-full mt-[6px]" />
										<div className="flex items-start gap-1">
											{storeCountryCode ? (
												<Flag
													className="object-cover border border-solid border-gray-10 rounded"
													code={storeCountryCode}
													width={20}
													height={14}
												/>
											) : null}
											<SypacText variant="body-regular-medium">
												<p className="text-gray-50 text-[14px] leading-[16px]">
													{storeAddress}
												</p>
											</SypacText>
										</div>
									</div>
								) : null}
							</div>
						</div>
						<DashedDivider dashLength={5} gapLength={5} />

						<div className="h-[16px] grid grid-cols-[126px,auto] gap-5 items-center">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40 text-[14px] leading-[16px]">
									<T keyName="adminContracts.productName">Product name:</T>
								</p>
							</SypacText>

							<SypacText variant="body-regular-medium">
								<p className="text-gray-80 text-[14px] leading-[16px]">
									{productName}
								</p>
							</SypacText>
						</div>

						<div className="h-[16px] grid grid-cols-[126px,auto] gap-5 items-center">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40 text-[14px] leading-[16px]">
									<T keyName="adminContracts.contractQuantity">
										Contract quantity:
									</T>
								</p>
							</SypacText>

							<SypacText variant="body-regular-medium">
								<p className="text-gray-80 text-[14px] leading-[16px]">
									{quantity} <T keyName="basicDetails.tons">tons</T>
								</p>
							</SypacText>
						</div>

						{overdraftedQty !== null ? (
							<div className="h-[16px] grid grid-cols-[126px,auto] gap-5 items-center">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40 text-[14px] leading-[16px] whitespace-nowrap">
										<T keyName="adminContracts.overdraftedQuantity">
											Overdrafted quantity:
										</T>
									</p>
								</SypacText>

								<SypacText variant="body-regular-medium">
									<p className="text-gray-80 text-[14px] leading-[16px]">
										<NumericFormat
											type="text"
											displayType="text"
											thousandSeparator=" "
											decimalSeparator="."
											decimalScale={2}
											fixedDecimalScale={false}
											value={overdraftedQty}
										/>{' '}
										<T keyName="basicDetails.tons">tons</T>
									</p>
								</SypacText>
							</div>
						) : null}

						<DashedDivider dashLength={5} gapLength={5} />

						<div className="h-[16px] grid grid-cols-[126px,auto] gap-5 items-center">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40 text-[14px] leading-[16px]">
									<T keyName="adminContracts.buyerPrice">Buyer price:</T>
								</p>
							</SypacText>
							<SypacText variant="body-regular-medium">
								<p className="text-gray-80 text-[14px] leading-[16px]">
									<NumericFormat
										type="text"
										displayType="text"
										thousandSeparator=" "
										decimalSeparator="."
										decimalScale={2}
										value={pricePerTonBuyer || 0}
										suffix={countryCode === 'MD' ? ' MDL' : ' PLN'}
									/>
								</p>
							</SypacText>
						</div>

						<div className="h-[16px] grid grid-cols-[126px,auto] gap-5 items-center">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40 text-[14px] leading-[16px]">
									<T keyName="adminContracts.sellerPrice">Seller price:</T>
								</p>
							</SypacText>

							<SypacText variant="body-regular-medium">
								<p className="text-gray-80 text-[14px] leading-[16px]">
									<NumericFormat
										type="text"
										displayType="text"
										thousandSeparator=" "
										decimalSeparator="."
										decimalScale={2}
										value={pricePerTonSeller || 0}
										suffix={countryCode === 'MD' ? ' MDL' : ' PLN'}
									/>
								</p>
							</SypacText>
						</div>

						<div className="h-[16px] grid grid-cols-[126px,auto] gap-5 items-center">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40 text-[14px] leading-[16px]">
									<T keyName="adminContracts.sypacMargin">SYPAC margin:</T>
								</p>
							</SypacText>

							<SypacText variant="body-regular-medium">
								<p className="text-gray-80 text-[14px] leading-[16px]">
									<NumericFormat
										type="text"
										displayType="text"
										thousandSeparator=" "
										decimalSeparator="."
										value={markup ?? 0}
										suffix=" %"
									/>
								</p>
							</SypacText>
						</div>

						{allowedOverdraftPercent !== undefined &&
						allowedOverdraftPercent !== null ? (
							<div className="h-[16px] grid grid-cols-[126px,auto] gap-5 items-center">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40 text-[14px] leading-[16px]">
										<T keyName="adminContracts.overdraft">Overdraft:</T>
									</p>
								</SypacText>

								<SypacText variant="body-regular-medium">
									<p className="text-gray-80 text-[14px] leading-[16px]">
										<NumericFormat
											type="text"
											displayType="text"
											thousandSeparator=" "
											decimalSeparator="."
											value={allowedOverdraftPercent}
											suffix=" %"
										/>
									</p>
								</SypacText>
							</div>
						) : null}

						<div className="h-[16px] grid grid-cols-[126px,auto] gap-5 items-center">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40 text-[14px] leading-[16px]">
									<T
										keyName={
											isLoss
												? 'adminContracts.sypacLoss'
												: 'adminContracts.sypacProfit'
										}
									>
										{isLoss ? 'SYPAC Loss:' : 'SYPAC profit:'}
									</T>
								</p>
							</SypacText>

							<SypacText variant="body-regular-medium">
								<p className="text-gray-80 text-[14px] leading-[16px]">
									<NumericFormat
										type="text"
										displayType="text"
										thousandSeparator=" "
										decimalSeparator="."
										decimalScale={2}
										fixedDecimalScale={false}
										value={numberTwoDecimal(computedProfit)}
										prefix={isLoss ? '-' : ''}
										suffix={countryCode === 'MD' ? ' MDL' : ' PLN'}
										allowNegative={false}
									/>
								</p>
							</SypacText>
						</div>
						<DashedDivider dashLength={5} gapLength={5} />

						<div className="h-[16px] grid grid-cols-[126px,auto] gap-5 items-center">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40 text-[14px] leading-[16px]">
									<T keyName="adminContracts.contractDateFrom">
										Contract date from:
									</T>
								</p>
							</SypacText>

							<div className="h-[16px] flex items-center gap-3">
								<Calendar />
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80 text-[14px] leading-[16px] mt-[2px]">
										{dayjs(dateFrom).format('DD.MM.YYYY')}
									</p>
								</SypacText>
							</div>
						</div>

						<div className="h-[16px] grid grid-cols-[126px,auto] gap-5 items-center">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40 text-[14px] leading-[16px]">
									<T keyName="adminContracts.contractDateTo">
										Contract date to:
									</T>
								</p>
							</SypacText>

							<div className="h-[16px] flex items-center gap-3">
								<Calendar />
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80 text-[14px] leading-[16px] mt-[2px]">
										{dayjs(dateTo).format('DD.MM.YYYY')}
									</p>
								</SypacText>
							</div>
						</div>
					</div>
				</div>

				<div className="flex border-0 border-t border-solid border-gray-10 p-3">
					<SypacButton variant="primary" size="small" className="w-full">
						<button
							type="button"
							className="w-full px-6 py-[10px] rounded-lg"
							onClick={onConfirm}
						>
							<SypacText
								variant="body-regular-medium"
								className="flex items-center"
							>
								<p>
									<T keyName="adminContracts.gotIt">Got it</T>
								</p>
							</SypacText>
						</button>
					</SypacButton>
				</div>
			</div>
		</section>
	);
};

export default SuccessMessage;
